import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Flex } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useFormik } from 'formik';

import {
  useCancelHrmRequestMutation,
  useDeleteHrmRequestMutation,
  useGetHrmRequestFormQuery,
  useSubmitHrmRequestMutation,
} from '@Store/api/basicHrmApiSlice';
import { usePostOverseasFormMutation } from '@Features/EmployeeSelfService/requests/OverseasMission/store/api/overseasRequestApiSlice';
import {
  useDeleteDocumentByIdMutation,
  useFormListMutation,
  useFormUploadMutation,
} from '@Store/api/dmsApiSlice';
import {
  useAppDispatch,
  useAppSelector,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { useGetUserInfoMutation } from '@Store/api/userApiSlice';
import {
  getCountriesDictSelector,
  getCompaniesDictSelector,
  getOverseasSponsorsDictSelector,
} from '@Store/selectors/dictionarySelector';
import { getTotalDays } from '@Utils/transformDateTime';
import {
  transformDictToOptions,
  transformCountriesDictToOptions,
  transformCompaniesDictToOptions,
} from '@Utils/transformToOptions';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { MainForm, RequiredNotification } from '@Components/index';
import { RcCustomRequestOptions } from '@Components/types/UploadType';
import { DocumentFormUploadResponse, RequestError } from '@Types/api/form';
import { overseasMissionActions } from '@Features/EmployeeSelfService/requests/OverseasMission/store/overseasMissionSlice';
import { generateFilepath } from '@Utils/generateFilepath';
import { fileUploadActions } from '@Store/slices/fileUploadSlice';
import { currentFileUploadSelector } from '@Store/selectors/fileUploadSelector';
import { SponsorTypeCodes } from '@Enum/api/SponsorTypeCodes.enum';
import { NotificationContext } from '@Context/NotificationContext';
import { FooterActions } from '@Enum/index';
import { Attachment } from '@Components/types/Attachment';
import { getDocument } from '@Features/utils/document';
import { getFilepath } from '@Features/utils/getFilepath';
import { getEditFormType } from '@Features/utils/getEditFormType';
import { OverseasStatusCodes } from '@Enum/api/OverseasStatusCodes.enum';
import { validateForm } from '@Features/utils/validateForm';

import OverseasDetailsSection from '../components/overseasDetailSection';
import { OverseasMissionFormValues } from '../types';
import { initialValuesForm } from '../initialValuesForm';
import {
  getDocumentFolder,
  getFormUploadFileMetaData,
} from '../utils/document';
import { mapSponsoredByExtraField } from '../utils/mapSponsoredByExtraField';
import { OverseasMissionFormSchema } from '../validationSchema';

const OverseasMissionEditForm: React.FC = () => {
  const {
    editSuccessNotification,
    errorNotification,
    deleteSuccessNotification,
    submitSuccessNotification,
    cancelSuccessNotification,
  } = useContext(NotificationContext);
  const { requestId } = useParams();
  const dispatch = useAppDispatch();

  const [disabledSaveForm, setDisableSaveForm] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<Attachment | undefined>(
    undefined
  );
  const [removeUploadedFile, setRemoveUploadedFile] = useState(false);

  const countriesDict = useAppSelector(getCountriesDictSelector);
  const companiesDict = useAppSelector(getCompaniesDictSelector);
  const currentFileUpload = useAppSelector(currentFileUploadSelector);
  const overseasSponsorsDict = useAppSelector(getOverseasSponsorsDictSelector);
  const currentUser = useAppSelector(currentUserSelector);

  const [cancelRequest] = useCancelHrmRequestMutation();
  const [submitForm] = useSubmitHrmRequestMutation();
  const [deleteDocument] = useDeleteDocumentByIdMutation();
  const [getFormList] = useFormListMutation();
  const [deleteRequest] = useDeleteHrmRequestMutation();
  const [getUser] = useGetUserInfoMutation();
  const [postOverseasForm] = usePostOverseasFormMutation();
  const [formUpload] = useFormUploadMutation();
  const [skipRequest] = useState(requestId);
  const { data: editDataForm, refetch } = useGetHrmRequestFormQuery(
    requestId!,
    {
      skip: !skipRequest,
      refetchOnMountOrArgChange: true,
    }
  );

  const [showDeleteNotification, setShowDeleteNotification] = useState(false);
  const requisitionInfo = useRequisitionInfoExistingReq(
    editDataForm,
    'Overseas'
  );
  const { handleBackToListRequests } = useNavigationToListRequests();

  const handleUploadedFileRemove = useCallback(
    (value: boolean | undefined) => {
      setRemoveUploadedFile(Boolean(uploadedFile) && Boolean(value));
    },
    [uploadedFile]
  );

  const countriesOptions = useMemo(
    () => transformCountriesDictToOptions(countriesDict),
    [countriesDict]
  );
  const companiesOptions = useMemo(
    () => transformCompaniesDictToOptions(companiesDict),
    [companiesDict]
  );
  const sponsorsOptions = useMemo(
    () => transformDictToOptions(overseasSponsorsDict),
    [overseasSponsorsDict]
  );

  useEffect(() => {
    setUploadedFile(undefined);
    setRemoveUploadedFile(false);
    dispatch(fileUploadActions.setFile(null));

    if (!editDataForm?.requestId || !editDataForm?.filepath) {
      return;
    }

    const documentFolderPath = getDocumentFolder(editDataForm.requestId);

    getFormList(documentFolderPath)
      .unwrap()
      .then((response) => {
        const matchedDocument = getDocument(response, editDataForm.filepath);

        if (matchedDocument) {
          setUploadedFile({
            id: matchedDocument.documentId,
            name: editDataForm.filepath,
          });
        }
      });
  }, [editDataForm]);

  useEffect(
    () => () => {
      dispatch(overseasMissionActions.setOverseasMission(null));
      dispatch(fileUploadActions.setFile(null));
    },
    [requestId]
  );

  const uploadFile = useCallback(
    (reqFileId: string): Promise<DocumentFormUploadResponse> | null => {
      const { formData, filepath } = currentFileUpload;

      if (!filepath) {
        return null;
      }

      const fileMetaData = getFormUploadFileMetaData(reqFileId, filepath);

      return formUpload({
        document: JSON.stringify(fileMetaData),
        body: formData,
      }).unwrap();
    },
    [currentFileUpload]
  );

  const handleSubmit = useCallback(
    (values: OverseasMissionFormValues): void => {
      const submitFormHandler = async (): Promise<void> => {
        const sponsoredByExtraMapped = mapSponsoredByExtraField(
          values.sponsoredBy as string,
          values.sponsoredByExtra,
          companiesOptions
        );
        const filepath = getFilepath(
          currentFileUpload.filepath,
          removeUploadedFile,
          editDataForm?.filepath
        );

        const body = {
          requestId: editDataForm?.requestId,
          empNumber: editDataForm?.empNumber as string,
          currentLocation: editDataForm?.currentLocation,
          destination: values.placeOfTravel,
          travelDate: values.dateOfTravel,
          returnDate: values.returnDate,
          totDays: values.totDays,
          remarks: values.purposeOfTravel,
          eligibleDays: values.approvedTravelAmount as number,
          leaveTypeID: values.country as string,
          ticketRequired: values.sponsoredBy as string,
          filepath,
          ...sponsoredByExtraMapped,
        };

        setDisableSaveForm(true);

        try {
          const {
            createdDate,
            reqStatusId,
            requestId: reqId,
          } = await postOverseasForm(body).unwrap();

          dispatch(
            overseasMissionActions.setOverseasMission({
              createdDate,
              requestId: reqId,
              reqStatusId,
            })
          );

          if (removeUploadedFile) {
            await deleteDocument(uploadedFile!.id).unwrap();
          }

          await uploadFile(String(reqId));

          await submitForm(String(reqId));

          submitSuccessNotification(String(reqId));

          setDisableSaveForm(false);

          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      submitFormHandler();
    },
    [
      companiesOptions,
      handleBackToListRequests,
      editDataForm,
      currentFileUpload,
      uploadFile,
      removeUploadedFile,
      uploadedFile,
    ]
  );

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    validationSchema: OverseasMissionFormSchema,
    onSubmit: handleSubmit,
  });

  const handleSave = useCallback(async (): Promise<void> => {
    const isFormValid = await validateForm(OverseasMissionFormSchema, formik);

    if (!isFormValid) return;

    const sponsoredByExtraMapped = mapSponsoredByExtraField(
      formik.values.sponsoredBy as string,
      formik.values.sponsoredByExtra,
      companiesOptions
    );
    const filepath = getFilepath(
      currentFileUpload.filepath,
      removeUploadedFile,
      editDataForm?.filepath
    );
    const body = {
      requestId: editDataForm?.requestId,
      empNumber: editDataForm?.empNumber as string,
      currentLocation: editDataForm?.currentLocation,
      destination: formik.values.placeOfTravel,
      travelDate: formik.values.dateOfTravel,
      returnDate: formik.values.returnDate,
      totDays: formik.values.totDays,
      remarks: formik.values.purposeOfTravel,
      eligibleDays: formik.values.approvedTravelAmount as number,
      leaveTypeID: formik.values.country as string,
      ticketRequired: formik.values.sponsoredBy as string,
      filepath,
      ...sponsoredByExtraMapped,
    };

    setDisableSaveForm(true);

    try {
      const {
        requestId: reqId,
        createdDate,
        reqStatusId,
      } = await postOverseasForm(body).unwrap();

      dispatch(
        overseasMissionActions.setOverseasMission({
          createdDate,
          requestId: reqId,
          reqStatusId,
        })
      );

      if (removeUploadedFile) {
        await deleteDocument(uploadedFile!.id).unwrap();
      }

      await uploadFile(String(reqId));

      editSuccessNotification(String(reqId));

      setDisableSaveForm(false);

      refetch();
    } catch (error) {
      const message = (error as RequestError)?.data;
      errorNotification(message);

      setDisableSaveForm(false);
    }
  }, [
    companiesOptions,
    editDataForm,
    formik,
    currentFileUpload,
    uploadFile,
    removeUploadedFile,
    uploadedFile,
    OverseasMissionFormSchema,
  ]);

  useEffect(() => {
    const { empNumber, empName, occupation, currentLocation } = currentUser;

    formik.setFieldValue('employeeNumber', empNumber);
    formik.setFieldValue('employeeName', empName);
    formik.setFieldValue('designation', occupation);
    formik.setFieldValue('currentLocation', currentLocation);
  }, [currentUser]);

  const handleFileRemove = useCallback((): void => {
    dispatch(fileUploadActions.setFile(null));
  }, []);

  const handleFileRequest = useCallback(
    (options: RcCustomRequestOptions): void => {
      const { file, onSuccess, onError } = options;
      const cb = (): void => {};
      const successCb = onSuccess || cb;
      const errorCb = onError || cb;
      const formData = new FormData();
      formData.append('file', file);

      if (file) {
        const filepath = generateFilepath('Overseas', (file as RcFile).name);
        dispatch(
          fileUploadActions.setFile({
            uploadedFile: file as RcFile,
            formData,
            filepath,
          })
        );
        successCb('Ok');
      } else {
        errorCb(new Error('file not uploaded'));
      }
    },
    []
  );

  useEffect(() => {
    if (!editDataForm?.empNumber) {
      return;
    }

    getUser({ empNo: editDataForm.empNumber })
      .unwrap()
      .then((user) => {
        formik.setValues({
          employeeNumber: editDataForm.empNumber,
          employeeName: user.empName,
          designation: user.occupation,
          currentLocation: user.currentLocation,
          placeOfTravel: editDataForm.destination,
          dateOfTravel: editDataForm.travelDate,
          returnDate: editDataForm.returnDate,
          totDays: String(editDataForm.totDays),
          purposeOfTravel: editDataForm.remarks,
          approvedTravelAmount: editDataForm.eligibleDays,
          country: String(editDataForm.leaveTypeID),
          sponsoredBy: String(editDataForm.ticketRequired),
          sponsoredByExtra: String(
            editDataForm.ticketRequired === SponsorTypeCodes.COMPANY
              ? editDataForm.wagesMonth
              : editDataForm.batchNo
          ),
        });
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [editDataForm]);

  useEffect(() => {
    if (formik.values.dateOfTravel && formik.values.returnDate) {
      const totalDays = getTotalDays(
        formik.values.returnDate,
        formik.values.dateOfTravel
      );

      formik.setFieldValue('totDays', totalDays);
    } else {
      formik.setFieldValue('totDays', '');
    }
  }, [formik.values.dateOfTravel, formik.values.returnDate]);

  const showDeleteRequestPopup = (): void => {
    setShowDeleteNotification(true);
  };

  const handleDeleteRequest = useCallback((): void => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    deleteRequest(reqId)
      .unwrap()
      .then(() => {
        deleteSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [editDataForm, handleBackToListRequests]);

  const handleCancel = useCallback(() => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    cancelRequest(reqId)
      .unwrap()
      .then(() => {
        cancelSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [handleBackToListRequests, editDataForm]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.CANCEL) {
        handleCancel();
      }

      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSave();
      }

      if (action === FooterActions.DELETE_REQUEST) {
        showDeleteRequestPopup();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSave, handleCancel]
  );

  const isOpenStatus =
    !editDataForm || editDataForm.reqStatusId === OverseasStatusCodes.OPEN;
  const formType = getEditFormType(isOpenStatus);

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        disabledSaveForm={disabledSaveForm}
        onFooterAction={handleFooterAction}
        formType={formType}
      >
        <FormItems>
          <OverseasDetailsSection
            label="Overseas Mission Details"
            formik={formik}
            leaveTypeOptions={countriesOptions}
            onFileRequest={handleFileRequest}
            onFileRemove={handleFileRemove}
            sponsorOptions={sponsorsOptions}
            companyOptions={companiesOptions}
            uploadedFile={uploadedFile}
            onUploadedFileRemove={handleUploadedFileRemove}
          />
        </FormItems>
      </MainForm>
      <RequiredNotification
        title="Delete Request"
        message={`Are you sure you want to delete request no.${requestId}?`}
        isModalOpen={showDeleteNotification}
        textSuccessBtn="Yes, Delete"
        textCancelBtn="No, Thanks"
        handleCancel={() => setShowDeleteNotification(false)}
        handleSuccess={handleDeleteRequest}
        isDeleteNotification
      />
    </Flex>
  );
};

export default OverseasMissionEditForm;
